/**
 * HTTP Implementation of Profile Gateway
 *
 * Handles all profile management operations via REST API
 * Uses JWT authentication from auth state with automatic 401 retry
 *
 * Story 4.9: Session Expiration Handling
 * - Integrates httpInterceptor for automatic token refresh on 401
 * - Retries failed requests with refreshed token
 * - Calls onSessionExpired when refresh fails
 */

import type {
  IProfileGateway,
  UserProfile,
  AvatarUploadUrlResponse,
  AvatarConfirmResponse,
} from "@domain/ports/IProfileGateway";
import {
  createAuthenticatedFetch,
  type AuthenticatedFetch,
} from "./httpInterceptor";

export class HttpProfileGateway implements IProfileGateway {
  private baseUrl: string;
  private authenticatedFetch: AuthenticatedFetch;

  constructor(
    baseUrl: string,
    getAuthToken: () => string | null,
    refreshToken: () => Promise<string | null>,
    onSessionExpired: () => void,
  ) {
    this.baseUrl = baseUrl;
    this.authenticatedFetch = createAuthenticatedFetch({
      getToken: getAuthToken,
      refreshToken,
      onSessionExpired,
    });
  }

  private async request<T>(
    path: string,
    options: RequestInit = {},
  ): Promise<T> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    };

    console.log("[HttpProfileGateway] Making request:", {
      path,
      method: options.method || "GET",
    });

    // Use authenticated fetch with 401 retry logic
    const response = await this.authenticatedFetch(`${this.baseUrl}${path}`, {
      ...options,
      headers,
    });

    console.log("[HttpProfileGateway] Response status:", {
      path,
      status: response.status,
      ok: response.ok,
      statusText: response.statusText,
    });

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: "Unknown error" }));
      console.error("[HttpProfileGateway] Request failed:", error);
      // Extract error message from Zod validation format: { errors: [{ message: "..." }] }
      const errorMessage =
        error.errors?.[0]?.message ||
        error.error ||
        error.message ||
        `HTTP error ${response.status}`;
      throw new Error(errorMessage);
    }

    const jsonData = await response.json();
    console.log("[HttpProfileGateway] Parsed JSON:", {
      path,
      data: jsonData,
      dataType: typeof jsonData,
      isArray: Array.isArray(jsonData),
      keys: typeof jsonData === "object" ? Object.keys(jsonData) : "N/A",
    });

    return jsonData;
  }

  async getProfile(): Promise<UserProfile> {
    console.log("[HttpProfileGateway] Fetching profile from API...");

    // Define backend response structure
    interface BackendProfile {
      userId: string;
      username: string;
      email: string;
      avatar?: string;
      wallets: Array<{
        walletId: string;
        type: "ETH" | "SOL" | "BTC";
        address: string;
        ensName?: string;
        visibility: "public" | "private"; // ← Backend format (string)
        createdAt: string;
        updatedAt: string;
      }>;
      socialLinks: Array<{
        platform: string;
        url: string;
        visibility: "public" | "private"; // ← Backend format (string)
        createdAt: string;
        updatedAt: string;
      }>;
      createdAt: string;
    }

    const backendProfile = await this.request<BackendProfile>(
      "/api/v1/owner/profile",
    );

    console.log("[HttpProfileGateway] Backend response:", {
      hasUserId: "userId" in backendProfile,
      hasWallets: "wallets" in backendProfile,
      hasSocialLinks: "socialLinks" in backendProfile,
      walletsLength: backendProfile.wallets?.length,
      socialLinksLength: backendProfile.socialLinks?.length,
      wallets: backendProfile.wallets?.map((w) => ({
        type: w.type,
        address: w.address,
        ensName: w.ensName,
        visibility: w.visibility, // Backend string format
      })),
      socialLinks: backendProfile.socialLinks?.map((sl) => ({
        platform: sl.platform,
        visibility: sl.visibility, // Backend string format
      })),
    });

    // Map backend format to frontend UserProfile interface
    const profile: UserProfile = {
      userId: backendProfile.userId,
      username: backendProfile.username,
      email: backendProfile.email,
      avatar: backendProfile.avatar,

      // Map wallets: visibility (string) → visible (boolean)
      wallets: backendProfile.wallets.map((w) => ({
        id: w.walletId,
        type: w.type,
        address: w.address,
        ensName: w.ensName || "",
        visible: w.visibility === "public", // ✅ Map to boolean
        createdAt: w.createdAt,
      })),

      // Map social links: visibility (string) → visible (boolean)
      socialLinks: backendProfile.socialLinks.map((sl) => ({
        platform: sl.platform,
        url: sl.url,
        visible: sl.visibility === "public", // ✅ Map to boolean
        createdAt: sl.createdAt,
      })),

      createdAt: backendProfile.createdAt,
    };

    console.log("[HttpProfileGateway] Mapped profile:", {
      wallets: profile.wallets.map((w) => ({
        type: w.type,
        address: w.address,
        ensName: w.ensName,
        visible: w.visible, // Frontend boolean format
      })),
      socialLinks: profile.socialLinks.map((sl) => ({
        platform: sl.platform,
        visible: sl.visible, // Frontend boolean format
      })),
    });

    return profile;
  }

  async updateUsername(username: string): Promise<void> {
    await this.request<void>("/api/v1/owner/profile/username", {
      method: "PUT",
      body: JSON.stringify({ username }),
    });
  }

  async requestAvatarUploadUrl(
    fileType: string,
  ): Promise<AvatarUploadUrlResponse> {
    return this.request<AvatarUploadUrlResponse>(
      "/api/v1/owner/profile/avatar/upload-url",
      {
        method: "POST",
        body: JSON.stringify({ fileType }),
      },
    );
  }

  async confirmAvatarUpload(key: string): Promise<AvatarConfirmResponse> {
    return this.request<AvatarConfirmResponse>("/api/v1/owner/profile/avatar", {
      method: "PUT",
      body: JSON.stringify({ key }),
    });
  }

  async deleteAvatar(): Promise<void> {
    await this.request<void>("/api/v1/owner/profile/avatar", {
      method: "DELETE",
    });
  }

  async addWallet(
    type: "ETH" | "SOL" | "BTC",
    address: string,
    ensName?: string,
  ): Promise<import("@domain/ports/IProfileGateway").Wallet> {
    // Backend response format
    interface BackendWalletResponse {
      walletId: string;
      type: "ETH" | "SOL" | "BTC";
      address: string;
      ensName?: string;
      visibility: "public" | "private";
      createdAt: string;
      updatedAt: string;
    }

    // Normalize ensName: BTC wallets cannot have ENS names (must be null)
    // Empty strings should be null for all wallet types (backend Zod validation)
    const normalizedEnsName =
      type === "BTC"
        ? null // BTC wallets cannot have ENS names
        : ensName === ""
          ? null
          : ensName; // ETH/SOL: convert empty string to null

    const backendWallet = await this.request<BackendWalletResponse>(
      "/api/v1/owner/profile/wallet",
      {
        method: "PUT",
        body: JSON.stringify({ type, address, ensName: normalizedEnsName }),
      },
    );

    console.log(
      "[HttpProfileGateway] addWallet backend response:",
      backendWallet,
    );

    // Map backend format to frontend Wallet interface
    const wallet: import("@domain/ports/IProfileGateway").Wallet = {
      id: backendWallet.walletId,
      type: backendWallet.type,
      address: backendWallet.address,
      ensName: backendWallet.ensName || "",
      visible: backendWallet.visibility === "public", // Map "public"/"private" to boolean
      createdAt: backendWallet.createdAt,
    };

    console.log("[HttpProfileGateway] Mapped wallet:", wallet);
    return wallet;
  }

  async deleteWallet(type: "ETH" | "SOL" | "BTC"): Promise<void> {
    await this.request<void>(`/api/v1/owner/profile/wallet/${type}`, {
      method: "DELETE",
    });
  }

  async toggleWalletVisibility(
    type: "ETH" | "SOL" | "BTC",
    newVisibility: boolean,
  ): Promise<void> {
    const visibility = newVisibility ? "public" : "private";
    await this.request<void>(
      `/api/v1/owner/profile/wallet/${type}/visibility`,
      {
        method: "PATCH",
        body: JSON.stringify({ visibility }),
      },
    );
  }

  async addSocialLink(
    platform: string,
    url: string,
  ): Promise<import("@domain/ports/IProfileGateway").SocialLink> {
    // Backend response format
    interface BackendSocialLinkResponse {
      platform: string;
      url: string;
      visibility: "public" | "private";
      createdAt: string;
      updatedAt: string;
    }

    const backendSocialLink = await this.request<BackendSocialLinkResponse>(
      `/api/v1/owner/profile/social/${platform}`,
      {
        method: "PUT",
        body: JSON.stringify({
          platform,
          url,
        }),
      },
    );

    console.log(
      "[HttpProfileGateway] addSocialLink backend response:",
      backendSocialLink,
    );

    // Map backend format to frontend SocialLink interface
    const socialLink: import("@domain/ports/IProfileGateway").SocialLink = {
      platform: backendSocialLink.platform,
      url: backendSocialLink.url,
      visible: backendSocialLink.visibility === "public", // Map "public"/"private" to boolean
      createdAt: backendSocialLink.createdAt,
    };

    console.log("[HttpProfileGateway] Mapped social link:", socialLink);
    return socialLink;
  }

  async deleteSocialLink(platform: string): Promise<void> {
    await this.request<void>(`/api/v1/owner/profile/social/${platform}`, {
      method: "DELETE",
    });
  }

  async toggleSocialLinkVisibility(
    platform: string,
    newVisibility: boolean,
  ): Promise<void> {
    const visibility = newVisibility ? "public" : "private";
    await this.request<void>(
      `/api/v1/owner/profile/social/${platform}/visibility`,
      {
        method: "PATCH",
        body: JSON.stringify({ visibility }),
      },
    );
  }

  async exportUserData(): Promise<void> {
    await this.request<void>("/api/v1/owner/export", {
      method: "POST",
    });
  }

  async deleteAccount(): Promise<void> {
    await this.request<void>("/api/v1/owner/account", {
      method: "DELETE",
    });
  }
}
